import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import useGameStore from '../stores/gameStore';
import useUIStore from '../stores/uiStore';
import useAuthStore from '../stores/authStore';
import apiService from '../services/api';
import guestService from '../services/guestService';
import { config } from '../config';

interface WebSocketState {
  socket: Socket | null;
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  reconnectAttempts: number;
}

interface WebSocketActions {
  connect: (sessionId: string, playerId: string) => void;
  disconnect: () => void;
  joinSession: (sessionId: string, playerId: string) => void;
  leaveSession: (sessionId: string, playerId: string) => void;
  sendPlayerAction: (sessionId: string, playerId: string, optionId: string) => void;
  sendPlayerReady: (sessionId: string, playerId: string) => void;
}

// Guard so only one waiting-update handler processes guest login (backend may send many times; no await before check)
let guestLoginHandled = false;

export const useWebSocket = (): WebSocketState & WebSocketActions => {
  const navigate = useNavigate();
  const gameStore = useGameStore();
  const uiStore = useUIStore();
  const socketRef = useRef<Socket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const gameStoreRef = useRef(gameStore);
  const isConnectingRef = useRef(false);
  const autoTransitionInProgressRef = useRef(false);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 1000; // Start with 1 second

  // Keep the gameStore ref up to date
  gameStoreRef.current = gameStore;

  const connect = useCallback((sessionId: string, playerId: string) => {
    // If already connected, disconnect first so we reconnect with the current token.
    // This fixes the case where guest was recreated (e.g. after 404) and session/player
    // now belong to a new guest but the socket was authenticated with the old token.
    if (socketRef.current?.connected) {
      socketRef.current.disconnect();
      socketRef.current = null;
      gameStoreRef.current.setConnectionStatus(false);
    }

    if (isConnectingRef.current) {
      return; // Already connecting
    }

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    isConnectingRef.current = true;

    try {
      // Get current authentication token (guest or regular)
      const authState = useAuthStore.getState();
      let token: string | null = null;
      
      if (authState.isGuest) {
        // For guest users, use guest token from guestService or auth store
        const guestData = guestService.getGuestData();
        token = guestData?.token || authState.token;
      } else if (authState.isAuthenticated && authState.token) {
        // For authenticated users, use regular token from auth store
        token = authState.token;
      }

      // For guests we require a token before connecting (avoids failed handshakes when token
      // isn't ready yet) and use polling first for more reliable connection in dev/proxy setups.
      const isGuest = authState.isGuest;
      if (isGuest && !token) {
        isConnectingRef.current = false;
        return;
      }

      const transports: ('websocket' | 'polling')[] = isGuest
        ? ['polling', 'websocket']  // Polling first for guests: more reliable when connecting directly to backend
        : ['websocket', 'polling'];

      // Create socket with authentication token in handshake.
      // forceNew: true so we never reuse a connection that was authenticated with a different token
      // (e.g. after clearing a stale guest and creating a new one).
      const socket = io(config.websocketUrl, {
        transports,
        timeout: 10000,
        forceNew: true,
        ...(token && { auth: { token } }), // Send token in handshake.auth.token
        ...(token && { extraHeaders: { Authorization: `Bearer ${token}` } }), // Also send in Authorization header as fallback
      });

      socketRef.current = socket;

      // Connection events
      socket.on('connect', async () => {
        isConnectingRef.current = false;
        gameStore.setConnectionStatus(true);
        joinSession(sessionId, playerId);
        
        // After connecting, check for pending friend requests and reload scene if needed
        // This handles the case where a friend request was sent while the websocket was disconnected
        // We check immediately and also after a delay to catch any that arrive in between
        const checkPendingFriendRequests = async () => {
          if (!gameStoreRef.current.currentSession || !gameStoreRef.current.currentPlayer) {
            return;
          }
          
          const { default: useAuthStore } = await import('../stores/authStore');
          const authState = useAuthStore.getState();
          if (authState.isGuest) {
            return;
          }
          
          try {
            const friendsService = (await import('../services/friendsService')).default;
            const pendingRequests = await friendsService.getFriendRequests();
            if (pendingRequests.length > 0) {
              await gameStoreRef.current.loadCurrentScene();
            }
          } catch (error: any) {
            if (error?.response?.status !== 401 && error?.status !== 401) {
              console.error('[useWebSocket] Failed to check for pending friend requests:', error);
            }
          }
        };
        
        // Check immediately (in case friend request was sent before socket connected)
        checkPendingFriendRequests();
        
        // Also check after a delay to catch any that arrive while the socket is connecting
        setTimeout(checkPendingFriendRequests, 3000);

        // Refresh scene on connect/reconnect when we have session+player+scene
        // Picks up condition changes that occurred while disconnected (e.g. Player B completed action)
        const { currentSession, currentPlayer, currentScene } = gameStoreRef.current;
        if (currentSession && currentPlayer && currentScene &&
            currentSession.id === sessionId && currentPlayer.id === playerId) {
          try {
            await gameStoreRef.current.loadCurrentScene();
          } catch (error) {
            console.error('[useWebSocket] Failed to refresh scene on connect:', error);
          }
        }
      });

      socket.on('disconnect', (reason) => {
        gameStore.setConnectionStatus(false);
        
        // Reconnect on server/transport issues, not only explicit server disconnect
        // (transport close/error/ping timeout occur on server restart, network drop)
        if (['io server disconnect', 'transport close', 'transport error', 'ping timeout'].includes(reason)) {
          attemptReconnect(sessionId, playerId);
        }
      });

      socket.on('connect_error', (error) => {
        console.error('WebSocket connection error:', error);
        isConnectingRef.current = false;
        uiStore.setError({ code: 'WEBSOCKET_ERROR', message: 'Failed to connect to game server' });
        attemptReconnect(sessionId, playerId);
      });

      // Game events
      socket.on('join-confirmed', async (data: { shouldRefreshScene?: boolean }) => {
        // Session join confirmed - no toast needed
        // Refresh scene when backend signals it (e.g. rejoining after disconnect)
        if (data?.shouldRefreshScene) {
          const { currentSession, currentPlayer } = gameStoreRef.current;
          if (currentSession && currentPlayer) {
            try {
              await gameStoreRef.current.loadCurrentScene();
            } catch (error) {
              console.error('[useWebSocket] Failed to refresh scene on join-confirmed:', error);
            }
          }
        }
      });

      socket.on('leave-confirmed', (_data) => {
        uiStore.showToast('Left game session', 'info');
      });

      socket.on('state-update', async (data) => {
        gameStore.updateGameState(data);
        
        // Handle different types of state updates
        if (data.type === 'session_started') {
          // Session has started, reload session data and redirect to scene
          const { currentSession, currentPlayer } = gameStoreRef.current;
          
          if (currentSession && currentPlayer) {
            try {
              // Update session status
              gameStoreRef.current.setLoading(true);
              
              // Reload session players to get updated state
              await gameStoreRef.current.loadSessionPlayers(currentSession.id);
              
              // Navigate to the player's starting scene
              window.location.href = `/scene/${currentSession.id}/${currentPlayer.id}`;
            } catch (error) {
              console.error('Failed to handle session start:', error);
            }
          }
        } else if (data.type === 'player_joined' || data.type === 'player_left') {
          // Reload session players when someone joins or leaves
          const { currentSession } = gameStoreRef.current;
          if (currentSession) {
            try {
              await gameStoreRef.current.loadSessionPlayers(currentSession.id);
            } catch (error) {
              console.error('Failed to reload session players:', error);
            }
          }
        } else if (data.type === 'player_action' && data.gameState) {
          // Action completed; clear pending state and escalation timers so UI exits loading/overlay
          useUIStore.getState().clearActionPending();
          // If this was our leave action, clear session (backend already removed us)
          const { currentSession, currentPlayer } = gameStoreRef.current;
          if (data.action === 'LEAVE' && currentPlayer && data.playerId === currentPlayer.id) {
            gameStoreRef.current.clearSessionState();
            useUIStore.getState().showToast?.('You left the adventure.', 'success');
            return;
          }
          if (currentSession && currentPlayer) {
            try {
              await gameStoreRef.current.loadCurrentScene();
            } catch (error) {
              console.error('Failed to fetch updated scene:', error);
            }
          }
        } else if (data.type === 'player_completed') {
          // Finish option completed
          useUIStore.getState().clearActionPending();
        } else if (data.type === 'state-update') {
          // A player's state changed (e.g. navigated to a new scene) — reload our scene so
          // presence-based displays (like ScheduleEventAttendance) reflect the new state.
          const { currentSession, currentPlayer } = gameStoreRef.current;
          if (currentSession && currentPlayer) {
            try {
              await gameStoreRef.current.loadCurrentScene();
            } catch (error) {
              console.error('Failed to reload scene after player state change:', error);
            }
          }
        }
      });

      // Adventure invite events
      socket.on('adventure_invite_received', async () => {
        // Trigger UI update for adventure invites list
        window.dispatchEvent(new CustomEvent('adventure_invite:update'));
        
        // Reload scene to update conditional sections (like getAdventureInviteCount())
        const { currentSession, currentPlayer } = gameStoreRef.current;
        if (currentSession && currentPlayer) {
          try {
            await gameStoreRef.current.loadCurrentScene();
          } catch (error) {
            console.error('Failed to reload scene after adventure invite received:', error);
          }
        }
      });

      socket.on('adventure_invite_accepted', () => {
        // Trigger UI update for sent invites and session state
        window.dispatchEvent(new CustomEvent('adventure_invite:update'));
      });

      socket.on('adventure_invite_rejected', () => {
        // Trigger UI update for sent invites list
        window.dispatchEvent(new CustomEvent('adventure_invite:update'));
      });

      socket.on('adventure_invite_cancelled', () => {
        // Trigger UI update for received invites list
        window.dispatchEvent(new CustomEvent('adventure_invite:update'));
      });

      socket.on('waiting-update', async (data) => {
        // Handle authentication tokens if provided (e.g., from async login completion).
        // Guard synchronously (no await) so only one handler runs when backend sends multiple times.
        let tokensUpdated = false;
        if (data && data.token && data.refreshToken && data.userId) {
          if (guestLoginHandled) return;
          const authState = useAuthStore.getState();
          if (!authState.isGuest) return;
          guestLoginHandled = true;

          try {
            // Tear down guest socket so it never tries to join the new session with guest token
            if (socketRef.current) {
              socketRef.current.disconnect();
              socketRef.current = null;
              gameStoreRef.current.setConnectionStatus(false);
            }
            isConnectingRef.current = false;

            const authService = (await import('../services/auth')).default;
            (authService as any).setTokens(data.token, data.refreshToken);

            // Transition auth first so any effect that runs sees non-guest before session changes
            let user: any = null;
            try {
              user = await authService.getCurrentUser();
            } catch (userError) {
              console.warn('[useWebSocket] Could not get user info, but tokens are set:', userError);
            }
            const authStore = useAuthStore.getState();
            authStore.setAuthenticatedAfterGuestLogin(data.token, data.refreshToken, user);

            tokensUpdated = true;

            // Now load user's system session and navigate (store update will trigger connect with new token)
            const store = gameStoreRef.current;
            const isSystemAdventure = store.currentAdventure?.id === store.systemAdventureId;
            if (isSystemAdventure) {
              try {
                await store.loadSystemSession();
                const session = gameStoreRef.current.currentSession;
                const player = gameStoreRef.current.currentPlayer;
                if (session?.id && player?.id) {
                  navigate(`/scene/${session.id}/${player.id}`, { replace: true });
                  return;
                }
              } catch (err) {
                console.warn('[useWebSocket] Failed to load system session after login:', err);
              }
            }

            await new Promise(resolve => setTimeout(resolve, 500));
          } catch (tokenError) {
            console.error('[useWebSocket] Failed to update tokens from waiting-update event:', tokenError);
            guestLoginHandled = false; // allow retry on error
          }
        }

        // Clear action pending when async op completes (e.g. registration) so loading/overlay state is cleared
        useUIStore.getState().clearActionPending();

        // Refresh the current scene to get updated waiting options
        // This triggers condition re-evaluation, which will enable waiting options
        // when their conditions are met (e.g., async operations complete)
        const { currentSession, currentPlayer } = gameStoreRef.current;
        if (currentSession && currentPlayer) {
          // Optionally filter by playerId if provided in event data (use String() so number/string mismatch doesn't skip)
          if (data && data.playerId != null && currentPlayer.id != null && String(data.playerId) !== String(currentPlayer.id)) {
            return; // Event is for a different player
          }

          try {
            // If tokens were just updated, verify they're available before reloading scene
            if (tokensUpdated) {
              const verifyAuthService = (await import('../services/auth')).default;
              let hasToken = !!(verifyAuthService as any).jwtToken;
              if (!hasToken) {
                await new Promise(resolve => setTimeout(resolve, 300));
                const verifyAuthServiceAgain = (await import('../services/auth')).default;
                hasToken = !!(verifyAuthServiceAgain as any).jwtToken;
                if (!hasToken) {
                  return;
                }
              }
            }
            
            // Track previous waiting options before reloading
            const previousWaitingOptionIds = Array.isArray(gameStoreRef.current.waitingOptionIds)
              ? [...gameStoreRef.current.waitingOptionIds]
              : [];
            // Reload scene to trigger condition re-evaluation
            // This will update availableOptionIds and waitingOptionIds based on current conditions
            await gameStoreRef.current.loadCurrentScene();
            
            // Detect auto-transition opportunities after scene reload
            const currentState = gameStoreRef.current;
            const currentAvailableOptionIds = currentState.availableOptionIds || [];
            
            // Check for auto-transition in two scenarios:
            // 1. Options that moved from waiting to available (newly available)
            // 2. Options that are currently available with autoTransition (in case condition was already met)
            const newlyAvailableOptionIds = currentAvailableOptionIds.filter(
              optionId => previousWaitingOptionIds.includes(optionId)
            );
            
            // Find auto-transition options: first check newly available, then check all available options
            // This handles cases where the condition was already met when waiting-update arrived
            let autoTransitionOptionId: string | undefined;
            
            if (newlyAvailableOptionIds.length > 0) {
              autoTransitionOptionId = newlyAvailableOptionIds.find(optionId => {
                const option = currentState.optionsMap[optionId];
                return option?.autoTransition === true;
              });
            }
            
            if (!autoTransitionOptionId && currentAvailableOptionIds.length > 0) {
              for (const optionId of currentAvailableOptionIds) {
                const option = currentState.optionsMap[optionId];
                if (option?.autoTransition === true) {
                  autoTransitionOptionId = optionId;
                  break;
                }
              }
            }
              
            if (autoTransitionOptionId) {
              // Prevent concurrent transitions
              if (autoTransitionInProgressRef.current) {
                return; // Already processing an auto-transition
              }
              
              try {
                autoTransitionInProgressRef.current = true;
                
                // Re-validate condition: check if option is still in availableOptionIds
                // (This prevents race conditions where condition becomes false between detection and transition)
                const currentAvailableOptionIds = gameStoreRef.current.availableOptionIds;
                if (!currentAvailableOptionIds.includes(autoTransitionOptionId)) {
                  // Condition is no longer true, skip auto-transition
                  autoTransitionInProgressRef.current = false;
                  return;
                }
                
                // Flush any pending user inputs before transition
                if (typeof gameStoreRef.current.flushPendingUserInputs === 'function') {
                  await gameStoreRef.current.flushPendingUserInputs();
                }
                
                // Re-check condition one more time after flushing inputs
                const finalAvailableOptionIds = gameStoreRef.current.availableOptionIds;
                if (!finalAvailableOptionIds.includes(autoTransitionOptionId)) {
                  // Condition changed during input flush, skip auto-transition
                  autoTransitionInProgressRef.current = false;
                  return;
                }
                
                // Execute auto-transition using REST API directly (not via useGameState.selectOption)
                // This ensures updated tokens are used via axios interceptor
                // useGameState.selectOption might use WebSocket which has old tokens
                // If tokens were just updated, wait a bit more to ensure they're in the axios interceptor
                if (tokensUpdated) {
                  await new Promise(resolve => setTimeout(resolve, 300));
                }
                
                await gameStoreRef.current.selectOption(autoTransitionOptionId);
              } catch (error) {
                // Log error but don't crash - option remains available for manual selection
                console.error('Failed to execute auto-transition:', error);
              } finally {
                autoTransitionInProgressRef.current = false;
              }
            }
          } catch (error) {
            console.error('Failed to refresh scene after waiting update:', error);
            autoTransitionInProgressRef.current = false;
          }
        }
      });

      socket.on('item-change', async (data) => {
        // Debug: log item-change payload
        
        
        // Handle item change notifications with toast
        if (data.itemChanges && Array.isArray(data.itemChanges)) {
          const { currentSession, currentPlayer } = gameStoreRef.current;
          
          if (currentSession) {
            // Aggregate changes by itemTag
            const changesByTag = new Map<string, Set<'added' | 'removed'>>();
            for (const change of data.itemChanges) {
              if (!changesByTag.has(change.itemTag)) changesByTag.set(change.itemTag, new Set());
              changesByTag.get(change.itemTag)!.add(change.changeType);
            }

            // Dispatch UI refresh for current player inventory once per tag
            if (currentPlayer) {
              for (const [itemTag, changeSet] of changesByTag.entries()) {
                const effectiveChange: 'added' | 'removed' = changeSet.has('added') ? 'added' : 'removed';
                try {
                  window.dispatchEvent(new CustomEvent('inventory:update', {
                    detail: {
                      ownerId: currentPlayer.id,
                      ownerType: 'character',
                      itemTag,
                      changeType: effectiveChange
                    }
                  }));
                } catch (e) {
                  void e;
                }
              }
            }

            // Only show toasts for current player's inventory, and prefer 'added' if both present
            if (currentPlayer) {
              for (const [itemTag, changeSet] of changesByTag.entries()) {
                const effectiveChange: 'added' | 'removed' = changeSet.has('added') ? 'added' : 'removed';
                try {
                  const itemData = await apiService.getItemData(currentSession.id, itemTag);
                  if (effectiveChange === 'added') {
                    uiStore.showToast(`Added item "${itemData.name}"`, 'success', 5000);
                  } else {
                    uiStore.showToast(`Removed item "${itemData.name}"`, 'info', 5000);
                  }
                } catch (error) {
                  console.error('Failed to fetch item data for notification:', error);
                  if (effectiveChange === 'added') {
                    uiStore.showToast(`Added item "${itemTag}"`, 'success', 5000);
                  } else {
                    uiStore.showToast(`Removed item "${itemTag}"`, 'info', 5000);
                  }
                }
              }
            }
          }
        }
      });

      // Handle unified inventory events
      socket.on('inventory-update', async (data) => {
        // Debug: log inventory-update payload
        
        // Avoid unused destructuring; toasts handled in item-change. We still need gameStoreRef for dispatch context.
        
        // Always notify UI components first so lists can refetch regardless of itemTag presence
        if (data && data.ownerId && data.ownerType) {
          try {
            window.dispatchEvent(new CustomEvent('inventory:update', {
              detail: {
                ownerId: data.ownerId,
                ownerType: data.ownerType,
                itemTag: data.itemTag,
                changeType: data.changeType
              }
            }));
          } catch (e) {
            void e;
          }
        }

        // Also surface a toast for unified events when an itemTag is present
        // This ensures tests and UIs that rely on unified events still get user feedback
        try {
          const { currentSession } = gameStoreRef.current;
          if (currentSession && data && data.itemTag) {
            const itemData = await apiService.getItemData(currentSession.id, data.itemTag);
            const changeType: 'added' | 'removed' | undefined = data.changeType;
            let targetLabel = '';
            if (data.ownerType === 'character') {
              const player = currentSession.players?.find(p => p.id === data.ownerId);
              targetLabel = player?.characterName || 'Character';
            } else if (data.ownerType === 'location') {
              targetLabel = data.ownerId || 'location';
            }

            const verb = changeType === 'removed' ? 'Removed' : 'Added';
            const level = changeType === 'removed' ? 'info' : 'success';
            uiStore.showToast(`${verb} "${itemData.name}" ${data.ownerType === 'character' ? `to ${targetLabel}` : `from ${targetLabel}`}`, level as any, 5000);
          }
        } catch (error) {
          // Fall back to itemTag if item data lookup fails
          const { currentSession } = gameStoreRef.current;
          if (currentSession && data && data.itemTag) {
            const changeType: 'added' | 'removed' | undefined = data.changeType;
            let targetLabel = '';
            if (data.ownerType === 'character') {
              const player = currentSession.players?.find(p => p.id === data.ownerId);
              targetLabel = player?.characterName || 'Character';
            } else if (data.ownerType === 'location') {
              targetLabel = data.ownerId || 'location';
            }
            const verb = changeType === 'removed' ? 'Removed' : 'Added';
            const level = changeType === 'removed' ? 'info' : 'success';
            uiStore.showToast(`${verb} "${data.itemTag}" ${data.ownerType === 'character' ? `to ${targetLabel}` : `from ${targetLabel}`}`, level as any, 5000);
          }
        }
      });

      socket.on('user-input-update', (data) => {
        if (!data || !data.variableName) {
          return;
        }
        // Only apply updates for the current player. Character/player-scoped variables
        // are per-player; applying the other player's update would overwrite our value.
        const currentPlayer = gameStoreRef.current.currentPlayer;
        if (currentPlayer && data.playerId && data.playerId !== currentPlayer.id) {
          if (import.meta.env.DEV && (data.scope === 'character' || data.scope === 'player')) {
            console.log('[WS user-input-update] Ignoring update for other player', {
              variableName: data.variableName,
              scope: data.scope,
              updatePlayerId: data.playerId,
              currentPlayerId: currentPlayer.id,
            });
          }
          return;
        }
        gameStoreRef.current.setUserVariableValue(
          data.variableName,
          data.value ?? '',
          data.scope
        );
      });

      // Handle friend request events
      socket.on('friend_request_received', async (data) => {
        // Notify UI components to refresh friend requests
        window.dispatchEvent(new Event('friend_request:update'));
        
        if (gameStoreRef.current.currentSession && gameStoreRef.current.currentPlayer) {
          try {
            await gameStoreRef.current.loadCurrentScene();
          } catch (error) {
            console.error('[useWebSocket] Failed to reload scene after friend request:', error);
          }
        }
        
        if (data && data.request) {
          uiStore.showToast('You received a new friend request', 'info', 5000);
        }
      });

      socket.on('friend_request_accepted', (data) => {
        // Notify UI components to refresh friends and sent requests
        window.dispatchEvent(new Event('friend_request:update'));
        window.dispatchEvent(new Event('friend:update'));
        if (data && data.request) {
          uiStore.showToast('Your friend request was accepted', 'success', 5000);
        }
      });

      socket.on('friend_request_rejected', (data) => {
        // Notify UI components to refresh sent requests
        window.dispatchEvent(new Event('friend_request:update'));
        if (data && data.request) {
          uiStore.showToast('Your friend request was rejected', 'info', 5000);
        }
      });

      socket.on('friend_added', (data) => {
        // Notify UI components to refresh friends list
        window.dispatchEvent(new Event('friend:update'));
        if (data && data.friend) {
          uiStore.showToast('You are now friends', 'success', 5000);
        }
      });

      socket.on('friend_removed', (data) => {
        // Notify UI components to refresh friends list
        window.dispatchEvent(new Event('friend:update'));
        if (data && data.friend) {
          uiStore.showToast('Friend removed', 'info', 5000);
        }
      });

      socket.on('error', (error) => {
        console.error('WebSocket error:', error);
        uiStore.setError({ code: 'WEBSOCKET_ERROR', message: error.message });
      });

    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      isConnectingRef.current = false;
      uiStore.setError({ code: 'WEBSOCKET_ERROR', message: 'Failed to create connection' });
    }
  }, [gameStore, uiStore, navigate]);

  const disconnect = useCallback(() => {
    isConnectingRef.current = false;
    
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    
    if (reconnectTimeoutRef.current) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    gameStoreRef.current.setConnectionStatus(false);
  }, []); // No dependencies to prevent infinite loops

  const joinSession = useCallback((sessionId: string, playerId: string) => {
    if (socketRef.current?.connected) {
      const clientOffsetMinutes = -new Date().getTimezoneOffset(); // minutes east of UTC
      socketRef.current.emit('join-session', {
        sessionId,
        playerId,
        timeZoneOffsetMinutes: clientOffsetMinutes,
      });
    }
  }, []);

  const leaveSession = useCallback((sessionId: string, playerId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('leave-session', { sessionId, playerId });
    }
  }, []);

  const sendPlayerAction = useCallback((sessionId: string, playerId: string, optionId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('player-action', { sessionId, playerId, optionId });
    } else {
      uiStore.setError({ code: 'WEBSOCKET_ERROR', message: 'Not connected to game server' });
    }
  }, [uiStore]);

  const sendPlayerReady = useCallback((sessionId: string, playerId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('player-ready', { sessionId, playerId });
    }
  }, []);

  const attemptReconnect = useCallback((sessionId: string, playerId: string) => {
    if (gameStoreRef.current.isConnected) {
      return; // Already connected
    }

    const currentAttempts = gameStoreRef.current.reconnectAttempts;
    
    if (currentAttempts >= maxReconnectAttempts) {
      uiStore.setError({ 
        code: 'WEBSOCKET_ERROR', 
        message: 'Failed to reconnect after multiple attempts' 
      });
      return;
    }

    const delay = reconnectDelay * Math.pow(2, currentAttempts); // Exponential backoff
    
    // Update reconnect attempts
    gameStoreRef.current.setReconnectAttempts(currentAttempts + 1);
    
    reconnectTimeoutRef.current = window.setTimeout(() => {
      connect(sessionId, playerId);
    }, delay);
  }, [uiStore, connect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  // Track previous session/player IDs to detect changes
  const prevSessionIdRef = useRef<string | undefined>(undefined);
  const prevPlayerIdRef = useRef<string | undefined>(undefined);

  // For guests we must have a token before connecting; subscribe so effect re-runs when token appears
  const guestTokenReady = useAuthStore((state) => !state.isGuest || !!state.token || !!guestService.getGuestData()?.token);

  // Auto-connect when session and player are available
  useEffect(() => {
    const { currentSession, currentPlayer } = gameStoreRef.current;

    if (!currentSession || !currentPlayer) {
      return;
    }

    // For guests, do not attempt connect until we have a token (connect() would no-op anyway)
    const authState = useAuthStore.getState();
    if (authState.isGuest) {
      const token = guestService.getGuestData()?.token || authState.token;
      if (!token) {
        return;
      }
    }

    const sessionId = currentSession.id;
    const playerId = currentPlayer.id;

    // Check if session or player has changed
    const sessionChanged = prevSessionIdRef.current !== undefined && prevSessionIdRef.current !== sessionId;
    const playerChanged = prevPlayerIdRef.current !== undefined && prevPlayerIdRef.current !== playerId;

    // If session or player changed and we're connected, disconnect first
    if ((sessionChanged || playerChanged) && socketRef.current?.connected) {
      // Leave the old session before disconnecting
      if (prevSessionIdRef.current && prevPlayerIdRef.current) {
        leaveSession(prevSessionIdRef.current, prevPlayerIdRef.current);
      }
      disconnect();
    }

    // Update refs
    prevSessionIdRef.current = sessionId;
    prevPlayerIdRef.current = playerId;

    // Connect if not connected and not already connecting
    if (!gameStoreRef.current.isConnected && !isConnectingRef.current) {
      connect(sessionId, playerId);
    }
  }, [gameStore.currentSession?.id, gameStore.currentPlayer?.id, gameStore.isConnected, guestTokenReady, connect, disconnect, leaveSession]);

  // When guest identity changes (e.g. after clearing stale guest and creating new one), disconnect
  // so the next connect() uses the new token and backend accepts join-session.
  const guestUserId = useAuthStore((state) => state.guestUserId);
  const lastGuestUserIdRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const prev = lastGuestUserIdRef.current;
    lastGuestUserIdRef.current = guestUserId ?? null;
    if (prev !== undefined && prev !== guestUserId && socketRef.current?.connected) {
      disconnect();
    }
  }, [guestUserId, disconnect]);

  // Watch for auth state changes (guest -> authenticated) and reconnect websocket
  const isGuest = useAuthStore((state) => state.isGuest);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const wasGuestRef = useRef<boolean | null>(null);
  
  useEffect(() => {
    // Initialize ref on first render
    if (wasGuestRef.current === null) {
      wasGuestRef.current = isGuest;
      return;
    }
    
    if (wasGuestRef.current === true && isGuest === false && isAuthenticated) {
      const { currentSession, currentPlayer } = gameStoreRef.current;
      if (currentSession && currentPlayer && socketRef.current?.connected) {
        disconnect();
        setTimeout(() => {
          const { currentSession: session, currentPlayer: player } = gameStoreRef.current;
          if (session && player) {
            connect(session.id, player.id);
          }
        }, 500);
      }
    }
    
    // Update ref
    wasGuestRef.current = isGuest;
  }, [isGuest, isAuthenticated, connect, disconnect]);

  return {
    // State
    socket: socketRef.current,
    isConnected: gameStoreRef.current.isConnected,
    isConnecting: isConnectingRef.current || (!gameStoreRef.current.isConnected && socketRef.current !== null),
    error: gameStoreRef.current.error?.message || null,
    reconnectAttempts: gameStoreRef.current.reconnectAttempts,
    
    // Actions
    connect,
    disconnect,
    joinSession,
    leaveSession,
    sendPlayerAction,
    sendPlayerReady,
  };
};

export default useWebSocket;
